import { useState, useMemo, useEffect } from"react";
import"@/App.css";
import Sidebar from"@/components/Sidebar";
import SearchBar from"@/components/SearchBar";
import Welcome from"@/components/Welcome";
import AccountDetail from"@/components/AccountDetail";
import Logique from"@/components/Logique";
import Quiz from"@/components/Quiz";
import Simulator from"@/components/Simulator";
import Exercices from"@/components/Exercices";
import Stats from"@/components/Stats";
import Favorites from"@/components/Favorites";
import Glossaire from"@/components/Glossaire";
import FAQ from"@/components/FAQ";
import Reforme from"@/components/Reforme";
import Ecritures from"@/components/Ecritures";
import FluxComptable from"@/components/FluxComptable";
import Comparateur from"@/components/Comparateur";
import Calculateurs from"@/components/Calculateurs";
import RechercheCas from"@/components/RechercheCas";
import Methodologie from"@/components/Methodologie";
import Parcours from"@/components/Parcours";
import Assistant from"@/components/Assistant";
import Logo from"@/components/Logo";
import PCGMap from"@/components/PCGMap";
import Footer from"@/components/Footer";
import Legal from"@/components/Legal";
import CookieBanner from"@/components/CookieBanner";
import AdminQueue from"@/components/AdminQueue";
import { useFavorites } from"@/hooks/useFavorites";
import { useStats } from"@/hooks/useStats";
import { PCG_INDEX, hydrate as hydratePCG } from"@/lib/pcgUtils";
import { fetchAllAccounts } from"@/lib/pcgClient";
import { Menu, X } from"lucide-react";

export default function App() {
 // Hash router minimal : #admin → interface de relecture EC (LOT 2).
 const [hash, setHash] = useState(typeof window !== "undefined" ? window.location.hash : "");
 useEffect(() => {
  const onHash = () => setHash(window.location.hash);
  window.addEventListener("hashchange", onHash);
  return () => window.removeEventListener("hashchange", onHash);
 }, []);

 const [selectedNum, setSelectedNum] = useState(null);
 const [view, setView] = useState("home");
 const [mobileOpen, setMobileOpen] = useState(false);
 const [faqDeepLink, setFaqDeepLink] = useState(null);
 const [pcgReady, setPcgReady] = useState(false);
 const [pcgError, setPcgError] = useState(null);
 const { favs, toggle, isFav, clear } = useFavorites();
 const { stats, trackView, trackQuiz, trackExercice, reset: resetStats } = useStats();

 // Bootstrap : charge tout le PCG depuis l'API au premier rendu.
 useEffect(() => {
  let cancelled = false;
  fetchAllAccounts()
   .then((accounts) => {
    if (cancelled) return;
    hydratePCG(accounts);
    setPcgReady(true);
   })
   .catch((err) => {
    if (cancelled) return;
    console.error("Bootstrap PCG a échoué :", err);
    setPcgError(err?.message ||"Erreur inconnue");
   });
  return () => {
   cancelled = true;
  };
 }, []);

 // Redirection 301 logique : anciens numéros PCG dépréciés
 const ACCOUNT_REDIRECTS = {"6712":"6582","675":"657","777":"747","775":"757","671":"678","6711":"6581","6714":"654","791": null, // supprimé sans remplacement
 };

 const handleSelectAccount = (num) => {
 let target = num;
 if (Object.prototype.hasOwnProperty.call(ACCOUNT_REDIRECTS, num)) {
 target = ACCOUNT_REDIRECTS[num];
 if (target === null) {
 // Compte supprimé du PCG 2025 — revient à l'accueil
 setSelectedNum(null);
 setView("home");
 setMobileOpen(false);
 return;
 }
 }
 setSelectedNum(target);
 setView("account");
 setMobileOpen(false);
 };

 const handleChangeView = (v) => {
 setView(v);
 if (v !=="account") setSelectedNum(null);
 if (v !=="faq") setFaqDeepLink(null);
 setMobileOpen(false);
 };

 const handleOpenFAQ = (faqId) => {
 setFaqDeepLink(faqId || null);
 setView("faq");
 setMobileOpen(false);
 window.scrollTo({ top: 0, behavior:"smooth" });
 };

 const account = useMemo(
 () => (selectedNum ? PCG_INDEX[selectedNum] : null),
 [selectedNum, pcgReady]
 );

 // Hash router : après tous les hooks, on route.
 if (hash.startsWith("#admin")) {
  return <AdminQueue />;
 }

 // Loading gate : bloque le rendu tant que le PCG n'est pas hydraté.
 if (!pcgReady) {
  return (
   <div
    data-testid="pcg-loading"
    className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4"
   >
    <div className="text-center">
     <div className="inline-block w-8 h-8 border-4 border-[#15294F] border-t-transparent rounded-full animate-spin mb-4" />
     <p className="text-slate-700 font-body text-base">
      {pcgError
       ? `Chargement du plan comptable impossible : ${pcgError}`
       :"Chargement du plan comptable…"}
     </p>
     {pcgError && (
      <button
       data-testid="pcg-reload-btn"
       onClick={() => window.location.reload()}
       className="mt-4 px-4 py-2 bg-[#15294F] text-white rounded-md font-semibold"
      >
       Réessayer
      </button>
     )}
    </div>
   </div>
  );
 }

 return (
 <div className="min-h-screen bg-[#F8FAFC] font-body text-[#15294F] flex flex-col md:flex-row">
 {/* Mobile top bar */}
 <header className="md:hidden flex items-center justify-between p-4 border-b border-keyto bg-white sticky top-0 z-20">
 <Logo size="sm" onClick={() => handleChangeView("home")} />
 <button
 data-testid="mobile-menu-toggle"
 onClick={() => setMobileOpen((v) => !v)}
 className="p-2 border border-keyto rounded-md bg-white"
 >
 {mobileOpen ? <X size={18} /> : <Menu size={18} />}
 </button>
 </header>

 {/* Sidebar */}
 <div className={`${mobileOpen ?"block" :"hidden"} md:block`}>
 <Sidebar
 selectedNum={selectedNum}
 onSelect={handleSelectAccount}
 view={view}
 onChangeView={handleChangeView}
 favCount={favs.length}
 />
 </div>

 {/* Main */}
 <main className="flex-1 min-w-0 p-3 md:p-8 lg:p-10">
 <div className="max-w-6xl mx-auto w-full">
 <div className="mb-6">
 <SearchBar onSelect={handleSelectAccount} />
 </div>

 {view ==="home" && (
 <Welcome onSelect={handleSelectAccount} onChangeView={handleChangeView} />
 )}
 {view ==="account" && account && (
 <AccountDetail
 account={account}
 isFav={isFav(account.num)}
 onToggleFav={() => toggle(account.num)}
 onSelect={handleSelectAccount}
 onView={trackView}
 onOpenFAQ={handleOpenFAQ}
 />
 )}
 {view ==="account" && !account && (
 <div className="text-slate-500">
    Sélectionnez un compte dans l&apos;arborescence à gauche.
 </div>
 )}
 {view ==="logique" && (
 <Logique
 onSelectClass={() => {
 handleChangeView("home");
 window.scrollTo({ top: 0, behavior:"smooth" });
 }}
 />
 )}
 {view ==="quiz" && <Quiz onQuizResult={trackQuiz} />}
 {view ==="simulator" && <Simulator />}
 {view ==="ecritures" && <Ecritures onAccountClick={handleSelectAccount} />}
 {view ==="flux" && <FluxComptable />}
 {view ==="comparateur" && <Comparateur onSelect={handleSelectAccount} />}
 {view ==="carte" && <PCGMap onSelect={handleSelectAccount} />}
 {view ==="calculateurs" && <Calculateurs />}
 {view ==="recherche" && <RechercheCas onAccountClick={handleSelectAccount} />}
 {view ==="methodologie" && <Methodologie />}
 {view ==="parcours" && <Parcours onChangeView={handleChangeView} />}
 {view ==="exercices" && <Exercices onExerciceComplete={trackExercice} />}
 {view ==="glossaire" && <Glossaire />}
 {view ==="faq" && (
 <FAQ deepLinkId={faqDeepLink} onSelectAccount={handleSelectAccount} />
 )}
 {view ==="reforme" && <Reforme />}
 {view ==="stats" && (
 <Stats stats={stats} onReset={resetStats} onChangeView={handleChangeView} />
 )}
 {view ==="favorites" && (
 <Favorites
 favs={favs}
 onSelect={handleSelectAccount}
 onToggle={toggle}
 onClear={clear}
 />
 )}
 {(view ==="mentions" || view ==="cgu" || view ==="rgpd" || view ==="cookies") && (
 <Legal kind={view} />
 )}
 </div>
 <Footer onChangeView={handleChangeView} currentView={view} />
 </main>
 <Assistant onSelectAccount={handleSelectAccount} onChangeView={handleChangeView} />
 <CookieBanner onChangeView={handleChangeView} />
 </div>
 );
}
